import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    signal,
    computed,
    HostBinding,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { EventService } from "../../../services/event.service";
import { ListDataParams2 } from "../../../services/list.service";
import { Item } from "../../../pojo/Item";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { Bind } from "../../../decorators/bind.decorator";
import { Action, S25ListDataComponent } from "../../s25-table-list/s25.list.data.component";
import { S25QuickSearchComponent } from "../../s25-quick-search/s25.quick.search.component";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { S25EditableModule } from "../../s25-editable/s25.editable.module";
import { S25Util } from "../../../util/s25-util";

@TypeManagerDecorator("s25-ng-more-actions-related")
@Component({
    selector: "s25-ng-more-actions-related",
    template: `
        @if (!init()) {
            <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
        } @else {
            <span class="moreActionsHeader">
                {{ itemName }}&nbsp;{{ itemIds.length ? lang.inSet : lang.notInSet }}
            </span>

            @if (addRemoveType === "relatedEvents") {
                <div class="cn-label">
                    <label for="relatedSetName">Related Set Name</label>
                    <s25-ng-editable-text
                        [alwaysEditing]="false"
                        [allowEmpty]="true"
                        [(val)]="setMeta.itemName"
                        [hasCommit]="true"
                        [max]="40"
                        [onCommit]="saveSetName"
                        [fieldId]="'relatedSetName'"
                        [readonlyTemplate]="readonlySetName"
                    ></s25-ng-editable-text>

                    @if (showReturnToSet()) {
                        <button class="aw-button aw-button--outline c-margin-left--single" (click)="startHomeMode()">
                            Return to Set
                        </button>
                    }
                </div>

                <ng-template #readonlySetName let-setName>
                    @if (setName) {
                        {{ setName }}
                    } @else {
                        <span class="ng-item-name__set-placeholder">+ Add Set Name</span>
                    }
                </ng-template>
            }

            @if (showReturnToSet() && showSearch()) {
                <s25-ng-quick-search
                    [type]="'event'"
                    [compType]="'event-quick-search'"
                    [actions]="saveActions"
                ></s25-ng-quick-search>
            }

            @if (showAddButton()) {
                <div class="actionItems">
                    <button class="aw-button aw-button--primary" id="add-events-to-set" (click)="startAddMode()">
                        Add Events to Set
                    </button>

                    @if (itemIds?.length > 0) {
                        <button
                            class="aw-button aw-button--primary"
                            (click)="startRemoveMode()"
                            id="remove-events-from-set"
                        >
                            Remove Events from Set
                        </button>
                    }
                </div>
            }

            @if (errorMsg()) {
                <p class="ngRed">{{ errorMsg() }}</p>
            }

            @if (!showSearch() && itemIds.length > 0) {
                <s25-ng-list-data
                    [hasSelect]="showReturnToSet()"
                    [query]="tableQuery"
                    [actions]="saveActions"
                ></s25-ng-list-data>
            }
        }
    `,
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            gap: 5px;
            padding: 1em;
            max-height: 600px;
            overflow-y: auto;
        }

        :host.minHeight {
            min-height: 375px; /*Make sure the search dropdown is not cut off*/
        }

        .moreActionsHeader {
            font-weight: bold;
        }

        .actionItems {
            display: flex;
            gap: 1em;
            justify-content: center;
        }

        s25-ng-quick-search {
            width: 100%;
        }

        .ngRed {
            font-size: 90%;
            width: fit-content;
            margin: 0 auto;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [S25ListDataComponent, S25QuickSearchComponent, S25LoadingInlineModule, S25EditableModule],
})
export class S25MoreActionsRelatedComponent implements OnInit {
    @Input() itemId: number;
    @Input() addRemoveType: "relatedEvents" | "boundReservations";
    @Input() itemTypeId: Item.Id;
    init = signal(false);
    itemName: string;
    itemIds: number[] = []; //related item ids
    setMeta: S25ItemI = {};
    errorMsg = signal("");
    saveActions: Action[];
    filterApplied = false;
    tableQuery: ListDataParams2;

    @ViewChild(S25ListDataComponent) dataTable: S25ListDataComponent;

    // Dynamically set min-height class to make sure the search dropdown is not cut off
    @HostBinding("class.minHeight") get setActiveState() {
        return this.showReturnToSet() && this.showSearch();
    }

    constructor(
        private cd: ChangeDetectorRef,
        private elRef: ElementRef,
    ) {}

    // View state enum for better type safety
    private readonly VIEW_MODES = {
        DEFAULT: "default",
        ADD_MODE: "add",
        REMOVE_MODE: "remove",
    } as const;

    // Single view state signal
    private viewMode = signal<string>(this.VIEW_MODES.DEFAULT);

    // Computed signals for visibility and state
    showReturnToSet = computed(() => this.viewMode() !== this.VIEW_MODES.DEFAULT);
    showAddButton = computed(() => this.viewMode() === this.VIEW_MODES.DEFAULT);
    showSearch = computed(() => this.viewMode() === this.VIEW_MODES.ADD_MODE);

    action1ButtonText = signal("Show Selected");
    action1ButtonSubText = signal("Show only selected items in the list");

    setSaveActions(mode?: "add" | "remove") {
        switch (mode) {
            case "add":
                this.saveActions = [
                    {
                        type: "outline",
                        text: this.action1ButtonText,
                        subText: this.action1ButtonSubText,
                        classList: "c-margin-right--half",
                        action: async (table) => {
                            if (this.filterApplied) {
                                table.filterRows((r) => true);
                                this.action1ButtonText.set("Show Selected");
                                this.action1ButtonSubText.set("Show only selected items in the list");
                            } else {
                                table.filterRows((r) => table.selected.has(r.id));
                                this.action1ButtonText.set("Show All");
                                this.action1ButtonSubText.set("Show all search results");
                            }
                            this.filterApplied = !this.filterApplied;
                        },
                        activateOnChecked: true,
                    },
                    {
                        type: "outline",
                        text: signal("Add Selected"),
                        subText: signal("Add selected items to current current set"),
                        action: async (table) => {
                            const selectedIds = Array.from(table.selected) as number[];
                            const [resp, err] = await S25Util.Maybe(
                                EventService.relateEvents(selectedIds, this.itemId, undefined),
                            );
                            if (err) {
                                this.errorMsg.set(`Unable to relate events:  ${err}`);
                            } else {
                                await this.startHomeMode(true);
                                this.cd.markForCheck();
                            }
                        },
                        activateOnChecked: true,
                    },
                ];
                break;
            case "remove":
                this.saveActions = [
                    {
                        type: "outline",
                        text: signal("Remove Selected"),
                        subText: signal("Remove selected items from current set"),
                        action: async (table) => {
                            const selectedIds = Array.from(table.selected) as number[];
                            const [resp, err] = await S25Util.Maybe(this.removeItems(selectedIds));
                            if (err) {
                                this.errorMsg.set(`Unable to remove events:  ${err}`);
                                return;
                            }

                            await this.startHomeMode(true);
                            this.cd.markForCheck();
                        },
                        activateOnChecked: true,
                    },
                ];
                break;
            default:
                this.saveActions = [];
        }
    }

    @Bind
    saveSetName(newName: string) {
        return EventService.relateEvents([], this.itemId, undefined, newName);
    }

    removeItems(itemIds: number[]) {
        return EventService.unrelateEvents(itemIds);
    }

    async ngOnInit() {
        this.lang = this.LANG[this.addRemoveType];
        await this.getData();
        await this.startHomeMode();
        this.init.set(true);
        this.cd.detectChanges();
    }

    async getData() {
        const [itemName, itemIds, meta] = await Promise.all([
            EventService.getEventNameOrTitle(this.itemId),
            EventService.getRelatedEventIds(this.itemId),
            EventService.getRelatedEventSet(this.itemId),
        ]);

        this.itemName = itemName;
        this.setMeta = meta ?? {};
        this.itemIds = itemIds ?? [];
        this.tableQuery = {
            ...this.tableQuery,
            itemType: "event",
            query: `event_id=${[...this.itemIds, this.itemId].join("+")}`,
            page: 0,
            itemsPerPage: 100,
            comptype: "event-quick-search",
        };
    }

    async startHomeMode(forceRefresh: boolean = false) {
        this.errorMsg.set("");
        if (forceRefresh) {
            //Force the table to refresh its query
            this.itemIds = [];
            this.cd.detectChanges();
            await this.getData();
        }

        this.setSaveActions();
        this.viewMode.set(this.VIEW_MODES.DEFAULT);
    }

    startAddMode() {
        this.setSaveActions("add");
        this.viewMode.set(this.VIEW_MODES.ADD_MODE);
    }

    startRemoveMode() {
        this.setSaveActions("remove");
        this.viewMode.set(this.VIEW_MODES.REMOVE_MODE);
    }

    //leaving the bound lang here until bound reservations is migrated. JS had both features in one component which wasn't ideal
    lang: {
        helpLink: string;
        iconClass: string;
        defaultErrMsg: string;
        searchLabel: string;
        notInSet: string;
        inSet: string;
        comptype: string;
        numericContextId: boolean;
    };

    LANG = {
        boundReservations: {
            helpLink: "bound_reservations",
            iconClass: "ngBindIcon",
            defaultErrMsg:
                "You do not have sufficient privileges to alter relationships in this bound event set. Please contact your administrator.",
            searchLabel: "Find Events to Bind:",
            notInSet: "is not currently in a bound event set",
            inSet: "is in a bound event set",
            comptype: "bound-reservation-quick-search",
            numericContextId: false,
        },
        relatedEvents: {
            helpLink: "event_relations",
            iconClass: "ngManageRelatedEventsIcon",
            defaultErrMsg:
                "You do not have sufficient privileges to alter relationships in this related event set. Please contact your administrator.",
            searchLabel: "Find Events to Relate:",
            notInSet: "is not currently in a related event set",
            inSet: "is in a related event set",
            comptype: "event-quick-search",
            numericContextId: true,
        },
    };
}
